import type { UserRole, VendorStatus } from "@chinese-vendor/schemas";

export type AuthRedirectHref =
  | "/(auth)/login"
  | "/(auth)/change-password"
  | "/(staff)/(dashboard)"
  | "/(vendor)/(dashboard)"
  | "/(vendor)/pending-review"
  | "/(vendor)/pre-vetting/step-1";

export const STAFF_DASHBOARD_ROLES: UserRole[] = [
  "staff",
  "admin",
  "super_admin",
  "localization_staff",
];

export function hasStaffDashboardAccess(roles: UserRole[]): boolean {
  return roles.some((role) => STAFF_DASHBOARD_ROLES.includes(role));
}

export function getVendorRedirectPath(status: VendorStatus | null): AuthRedirectHref {
  switch (status) {
    case "approved":
    case "rejected":
      return "/(vendor)/(dashboard)";
    case "pending_review":
      return "/(vendor)/pending-review";
    case "pending_application":
    case "pending_registration":
    default:
      return "/(vendor)/pre-vetting/step-1";
  }
}

/**
 * Returns the href to redirect to if the vendor's current sub-route is
 * not permitted for their status, or null if no redirect is needed.
 * Mirrors the three guards that were previously inlined in the vendor layout.
 */
export function getVendorRouteGuardRedirect(
  status: VendorStatus | null,
  currentSubRoute: string | undefined,
  hasDismissedPendingReview = false
): AuthRedirectHref | null {
  if (hasDismissedPendingReview && currentSubRoute === "pending-review") {
    return "/(vendor)/(dashboard)";
  }
  if (status !== "pending_review" && currentSubRoute === "pending-review") {
    return "/(vendor)/(dashboard)";
  }
  if (status === "pending_review" && currentSubRoute === "pre-vetting") {
    return "/(vendor)/(dashboard)";
  }
  if (status === "pending_application" && currentSubRoute !== "pre-vetting") {
    return "/(vendor)/pre-vetting/step-1";
  }
  if ((status === "pending_registration" || !status) && currentSubRoute !== "pre-vetting") {
    return "/(vendor)/pre-vetting/step-1";
  }
  return null;
}

export function isVendorRouteAllowed(
  status: VendorStatus | null,
  currentSubRoute: string | undefined
): boolean {
  return getVendorRouteGuardRedirect(status, currentSubRoute) === null;
}

export function getPostLoginRedirectPath(
  roles: UserRole[],
  vendorStatus: VendorStatus | null,
  temporaryPasswordSet = false
): AuthRedirectHref {
  if (temporaryPasswordSet) {
    return "/(auth)/change-password";
  }

  if (hasStaffDashboardAccess(roles)) {
    return "/(staff)/(dashboard)";
  }

  if (roles.includes("vendor")) {
    return getVendorRedirectPath(vendorStatus);
  }

  return "/(auth)/login";
}
